/**
 * Readiness checklist for a brand: what it still needs before it can issue documents
 * and send emails on its own. Shown in Admin → Organisations.
 */
import { directSupabase } from '../lib/supabaseClient';
import type { Brand } from './organisationSettings';

export const REQUIRED_DOCUMENT_TYPES = ['Quotation', 'Estimate', 'Invoice'] as const;

export type ReadinessCheck = {
  key: string;
  label: string;
  ok: boolean;
  detail?: string;
};

export type ReadinessData = {
  /** Active email template types per organisation name. */
  emailTemplates: Record<string, string[]>;
  /** PDF settings document types per organisation id. */
  pdfConfigs: Record<string, string[]>;
  /** Document template types per organisation id. */
  documentTemplates: Record<string, string[]>;
  /** Organisation ids with an active numbering sequence, with its prefix. */
  sequences: Record<string, string>;
};

/** Email template types every brand should have: the full set any selectable brand uses. */
export function expectedEmailTypes(brands: Brand[], data: ReadinessData): string[] {
  const types = new Set<string>();
  for (const brand of brands) {
    if (!brand.isSelectable) continue;
    for (const type of data.emailTemplates[brand.name] || []) types.add(type);
  }
  return Array.from(types).sort();
}

export function brandReadiness(brand: Brand, brands: Brand[], data: ReadinessData): ReadinessCheck[] {
  const expected = expectedEmailTypes(brands, data);
  const own = new Set(data.emailTemplates[brand.name] || []);
  const missingEmails = expected.filter((type) => !own.has(type));
  const pdf = new Set(data.pdfConfigs[brand.id] || []);
  const missingPdf = REQUIRED_DOCUMENT_TYPES.filter((type) => !pdf.has(type));
  const templates = new Set(data.documentTemplates[brand.id] || []);
  const missingTemplates = REQUIRED_DOCUMENT_TYPES.filter((type) => !templates.has(type));
  const legalMissing = [
    !brand.legalEntityName && 'legal entity',
    !brand.companyNumber && 'company number',
    !brand.vatNumber && 'VAT number',
    !brand.registeredOffice && 'registered office',
  ].filter(Boolean) as string[];

  return [
    {
      key: 'mailbox',
      label: 'Sending mailbox',
      ok: !!brand.senderEmail,
      detail: brand.senderEmail || 'Not set: emails fall back to the Keystone Glazing mailbox.',
    },
    {
      key: 'emails',
      label: 'Email templates',
      ok: missingEmails.length === 0 && own.size > 0,
      detail: own.size === 0
        ? 'None yet: customer emails for this brand will fail.'
        : missingEmails.length
          ? `Missing ${missingEmails.length}: ${missingEmails.join(', ')}`
          : `${own.size} active`,
    },
    {
      key: 'pdf',
      label: 'PDF settings',
      ok: missingPdf.length === 0,
      detail: missingPdf.length ? `Missing: ${missingPdf.join(', ')}` : 'Quotation, Estimate and Invoice',
    },
    {
      key: 'templates',
      label: 'Document templates',
      ok: missingTemplates.length === 0,
      detail: missingTemplates.length ? `Missing: ${missingTemplates.join(', ')}` : 'Quotation, Estimate and Invoice',
    },
    {
      key: 'sequence',
      label: 'Numbering',
      ok: !!data.sequences[brand.id],
      detail: data.sequences[brand.id] ? `Prefix ${data.sequences[brand.id]}` : 'No active sequence: new references cannot be minted.',
    },
    {
      key: 'legal',
      label: 'Legal entity details',
      ok: legalMissing.length === 0,
      detail: legalMissing.length ? `Missing: ${legalMissing.join(', ')}` : `${brand.legalEntityName} · ${brand.companyNumber}`,
    },
  ];
}

function group<T extends Record<string, any>>(rows: T[], key: keyof T, value: keyof T): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const row of rows) {
    const k = String(row[key] ?? '');
    if (!k) continue;
    (out[k] ||= []).push(String(row[value]));
  }
  return out;
}

export async function loadReadinessData(): Promise<ReadinessData> {
  const [emails, pdf, templates, sequences] = await Promise.all([
    directSupabase.from('email_templates').select('organization_name, template_type').eq('is_active', true),
    directSupabase.from('pdf_export_config').select('organisation_id, document_type'),
    directSupabase.from('document_templates').select('organisation_id, document_type'),
    directSupabase.from('doc_sequences').select('organisation_id, prefix').eq('is_active', true),
  ]);
  for (const result of [emails, pdf, templates, sequences]) {
    if (result.error) throw new Error(result.error.message);
  }
  const sequenceMap: Record<string, string> = {};
  for (const row of (sequences.data || []) as Array<{ organisation_id: string; prefix: string }>) {
    sequenceMap[String(row.organisation_id)] = String(row.prefix);
  }
  return {
    emailTemplates: group((emails.data || []) as any[], 'organization_name', 'template_type'),
    pdfConfigs: group((pdf.data || []) as any[], 'organisation_id', 'document_type'),
    documentTemplates: group((templates.data || []) as any[], 'organisation_id', 'document_type'),
    sequences: sequenceMap,
  };
}
